import { useState } from "react";
import { Field, inputCls, inputErrorCls } from "./lims";

/**
 * Credential entry for the three signing points (issue #99).
 *
 * Approving work is an electronic signature, so the signer executes their
 * identification components at the moment of signing (21 CFR §11.200) rather
 * than riding the session they happen to have open. The meaning of the
 * signature is stated above the fields deliberately: a signer has to see what
 * they are attesting to before they type a password, not after.
 *
 * The authenticator code is asked for on the first signature of a session and
 * optional afterwards, matching §11.200(a)(1)(i) — all components at the
 * first signing of a continuous period of controlled system access, at least
 * one at each subsequent signing. It is left visible and optional rather than
 * hidden, because the server is the authority on which components it wants
 * and a signer whose session has expired needs somewhere to put the code.
 *
 * The full manifestation of the executed signature — printed name, role,
 * timestamp and meaning shown back on the record and on the COA — is #101.
 */
export function useSigning() {
  const [password, setPassword] = useState("");
  const [totpCode, setTotpCode] = useState("");
  const reset = () => { setPassword(""); setTotpCode(""); };
  // Only ever sent, never held beyond the request that uses it.
  const block = () => ({
    password,
    ...(totpCode.trim() ? { totp_code: totpCode.trim() } : {}),
  });
  return { password, setPassword, totpCode, setTotpCode, reset, block };
}

export default function SigningFields({ signing, meaning, error, disabled }) {
  return (
    <div className="rounded-md border border-slate-200 bg-white p-3 space-y-3">
      <p data-testid="signing-meaning" className="text-xs text-slate-600">
        You are signing this record as: <strong className="text-slate-900">{meaning}</strong>
      </p>
      <Field label="Password" error={error} errorTestId="signing-error">
        <input
          data-testid="signing-password-input"
          type="password"
          autoComplete="current-password"
          value={signing.password}
          onChange={(e) => signing.setPassword(e.target.value)}
          disabled={disabled}
          className={`${inputCls} ${error ? inputErrorCls : ""}`}
        />
      </Field>
      <Field label="Authenticator code (first signature of a session)">
        <input
          data-testid="signing-totp-input"
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          value={signing.totpCode}
          onChange={(e) => signing.setTotpCode(e.target.value)}
          disabled={disabled}
          className={inputCls}
        />
      </Field>
    </div>
  );
}
